import { useEffect, useMemo, useRef, useState } from 'react'

import _ from 'lodash'
import moment from 'moment'

import { useCreateActionContext } from '../../../contexts/action'
import { createValidationSchema } from '../../../helpers/validationSchemas'
import { useGetActionTypesQuery, useGetCustomFieldsQuery } from '../../../services/field'

function hasFieldValue(value) {
  if (value === undefined || value === null) {
    return false
  }

  if (typeof value === 'string') {
    return value.trim() !== ''
  }

  if (Array.isArray(value)) {
    return value.length > 0
  }

  if (moment.isMoment(value)) {
    return value.isValid()
  }

  if (typeof value === 'object') {
    return Object.keys(value).length > 0
  }

  return true
}

function buildVisibleGroups(customFields, values = {}) {
  const visibleFieldGroups = {
    ungrouped: {
      groups: [],
      currentIndex: -1
    }
  }

  customFields.forEach(({ isGroup, isGroupUnderGroup, groupType, fields }, fieldName) => {
    const isRepeatedGroup = isGroup && isGroupUnderGroup && !!groupType
    const groupKey = isRepeatedGroup ? groupType : 'ungrouped'

    if (!visibleFieldGroups[groupKey]) {
      visibleFieldGroups[groupKey] = {
        groups: [],
        currentIndex: 0
      }
    }

    const currentGroup = visibleFieldGroups[groupKey]

    currentGroup.groups.push({
      fieldName,
      ...(isRepeatedGroup
        ? {
            internalFieldNames: fields.map(({ internalFieldName }) => internalFieldName)
          }
        : {})
    })

    if (groupKey === 'ungrouped') {
      currentGroup.currentIndex = currentGroup.groups.length - 1
    }
  })

  Object.keys(visibleFieldGroups).forEach((groupType) => {
    if (groupType === 'ungrouped') {
      return
    }

    const groupState = visibleFieldGroups[groupType]

    let lastVisibleIndex = 0

    groupState.groups.forEach(({ internalFieldNames = [] }, groupIndex) => {
      const groupHasValue = internalFieldNames.some((internalFieldName) => hasFieldValue(values[internalFieldName]))

      if (groupHasValue) {
        lastVisibleIndex = Math.max(lastVisibleIndex, groupIndex)
      }
    })

    groupState.currentIndex = lastVisibleIndex
  })

  return visibleFieldGroups
}

function getVisibleFieldNames(visibleGroups) {
  return new Set(
    Object.values(visibleGroups).flatMap(({ groups, currentIndex }) =>
      groups.slice(0, currentIndex + 1).map(({ fieldName }) => fieldName)
    )
  )
}

export function useCustomFields({ values, errors, setValues, actionTypeId }) {
  const [formValues, setFormValues] = useState({})
  const [visibleGroups, setVisibleGroups] = useState({})
  const [initialized, setInitialized] = useState(false)

  const {
    currentUser: { parentMember },
    isReadOnly,
    isPreview,
    setValidationSchema,
    isNewAction
  } = useCreateActionContext()

  const { currentData: customFields, isLoading } = useGetCustomFieldsQuery({ actionTypeId }, { skip: !actionTypeId })

  const { data: actionTypesList } = useGetActionTypesQuery({
    ...(parentMember && {
      memberIds: parentMember.id
    })
  })

  const debouncedSetValues = useRef(_.debounce(setValues, 200)).current

  useEffect(() => {
    return () => {
      debouncedSetValues.cancel()
    }
  }, [debouncedSetValues])

  const prevActionTypeIdRef = useRef(actionTypeId)

  useEffect(() => {
    if (prevActionTypeIdRef.current === actionTypeId) return
    prevActionTypeIdRef.current = actionTypeId

    debouncedSetValues.cancel()
    setFormValues({})
    setVisibleGroups({})
    setInitialized(false)
    setValues('customFields', {})
  }, [actionTypeId, debouncedSetValues, setValues])

  function onChangeValue(valuesToUpdate) {
    setFormValues((previousValues) => {
      const newValues = {
        ...previousValues,
        ...valuesToUpdate
      }

      debouncedSetValues('customFields', newValues)
      return newValues
    })
  }

  useEffect(
    function initializeCustomFields() {
      if (!customFields || initialized) {
        return
      }

      let initialValues = {
        ...(values || {})
      }

      const initialVisibleGroups = buildVisibleGroups(customFields, initialValues)

      if (isNewAction) {
        const visibleFieldNames = getVisibleFieldNames(initialVisibleGroups)

        customFields.forEach(({ fields }, fieldName) => {
          if (!visibleFieldNames.has(fieldName)) {
            return
          }

          fields.forEach((field) => {
            const { internalFieldName, defaultValue, fieldTypes } = field

            if (initialValues[internalFieldName] !== undefined && initialValues[internalFieldName] !== null) {
              return
            }

            if (defaultValue === null || defaultValue === undefined || defaultValue === '') {
              return
            }

            const fieldType = fieldTypes?.internalTypeName

            if (fieldType === 'date') {
              const momentDate = moment(defaultValue)
              if (momentDate.isValid()) {
                initialValues[internalFieldName] = momentDate
              }
              return
            }
            initialValues[internalFieldName] = defaultValue
          })
        })
      }

      setFormValues(initialValues)
      setVisibleGroups(initialVisibleGroups)
      setInitialized(true)

      if (isNewAction && Object.keys(initialValues).length > 0) {
        debouncedSetValues('customFields', initialValues)
      }
    },
    [customFields, initialized, values, isNewAction, debouncedSetValues]
  )

  useEffect(
    function updateValidationSchema() {
      if (!customFields || Object.keys(visibleGroups).length === 0) {
        return
      }

      const allFields = Object.values(visibleGroups)
        .flatMap(({ groups, currentIndex }) =>
          groups.slice(0, currentIndex + 1).flatMap(({ fieldName }) => customFields.get(fieldName)?.fields || [])
        )
        .filter(Boolean)

      const updatedValidationSchema = createValidationSchema(allFields)

      setValidationSchema(updatedValidationSchema)
    },
    [visibleGroups, customFields, setValidationSchema]
  )

  useEffect(
    function scrollToFirstError() {
      if (!errors) {
        return
      }

      const errorKeys = Object.keys(errors)

      if (errorKeys.length === 0) {
        return
      }

      const firstErrorElement = document.getElementById(errorKeys[0])

      if (firstErrorElement) {
        firstErrorElement.scrollIntoView({
          behavior: 'smooth',
          block: 'center'
        })

        firstErrorElement.focus()
      }
    },
    [errors]
  )

  const actionName =
    actionTypeId && actionTypesList?.find(({ value }) => Number(value) === Number(actionTypeId))?.description

  const setGroupVisible = (groupType, fieldName) => {
    setVisibleGroups((previousVisibleGroups) => {
      const groupState = previousVisibleGroups[groupType]

      if (!groupState) {
        return previousVisibleGroups
      }

      const { groups, currentIndex } = groupState

      if (currentIndex >= groups.length - 1) {
        return previousVisibleGroups
      }

      if (fieldName) {
        const selectedGroupIndex = groups.findIndex((group) => group.fieldName === fieldName)

        if (selectedGroupIndex !== currentIndex) {
          return previousVisibleGroups
        }
      }

      return {
        ...previousVisibleGroups,

        [groupType]: {
          ...groupState,
          currentIndex: currentIndex + 1
        }
      }
    })
  }

  const setGroupHidden = (groupType, fieldName) => {
    const groupState = visibleGroups[groupType]

    if (!groupState) {
      return
    }

    const { groups, currentIndex } = groupState

    if (currentIndex <= 0) {
      return
    }

    let targetGroupIndex = currentIndex

    if (fieldName) {
      const selectedGroupIndex = groups.findIndex((group) => group.fieldName === fieldName)

      if (selectedGroupIndex === -1) {
        return
      }

      if (selectedGroupIndex !== currentIndex) {
        return
      }

      targetGroupIndex = selectedGroupIndex
    }

    const targetGroup = groups[targetGroupIndex]

    if (!targetGroup) {
      return
    }

    const newValues = {
      ...formValues
    }

    targetGroup.internalFieldNames?.forEach((internalFieldName) => {
      newValues[internalFieldName] = null
    })

    setFormValues(newValues)

    debouncedSetValues('customFields', newValues)

    setVisibleGroups((previousVisibleGroups) => ({
      ...previousVisibleGroups,

      [groupType]: {
        ...previousVisibleGroups[groupType],
        currentIndex: targetGroupIndex - 1
      }
    }))
  }

  const visibleFields = useMemo(() => {
    if (!customFields || Object.keys(visibleGroups).length === 0) {
      return []
    }

    const visible = Object.values(visibleGroups).flatMap(({ groups, currentIndex }) =>
      groups.slice(0, currentIndex + 1).map(({ fieldName }) => fieldName)
    )

    return Array.from(customFields.entries()).filter(([fieldName]) => visible.includes(fieldName))
  }, [visibleGroups, customFields])

  return {
    formValues,
    setFormValues,
    visibleGroups,
    setVisibleGroups,
    initialized,
    setInitialized,
    customFields,
    isLoading,
    actionTypesList,
    debouncedSetValues,
    onChangeValue,
    actionName,
    setGroupVisible,
    setGroupHidden,
    visibleFields,
    isReadOnly,
    isPreview
  }
}
